import React, { useEffect, useState, useCallback, useRef } from "react";
import { Modal, Button, Spinner, Table, Card, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import blogFetch from "../../axios/config";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import QRCodeReaderOrdem from "./QRCodeReaderOrdem";

interface Peca {
    ordem: string;
    chaveModApont: string;
    proximoCenTrab: string;
    lote: string;
    material: string;
    descricao: string;
    tipo: string;
}

interface Componente {
    desenho_Key?: number;
    lote?: string;
    pacitem_key?: number;
    loteRefugo?: string;
    veioDoBanco?: boolean; // Essa flag será calculada ao carregar
    desenho_OrdemSerial?: string;
    desenho_Operation?: string;
    desenho_ClaveModelo?: string;
    adicionaL_1?: string;
    adicionaL_2?: string;
    adicionaL_3?: string;
    adicionaL_5?: string;
    desenho_CodMat?: string;
    desenho_Descricao?: string;
    desenho_ZTipo?: boolean;
    loteInvalido?: boolean;
}

const EMPTY_PECA: Peca = {
    ordem: "", chaveModApont: "", proximoCenTrab: "",
    lote: "", material: "", descricao: "", tipo: "",
};

interface Props {
    ordemInicial?: string;
    onApontamentoCompleted?: () => void;
}

export const TabelaResumoOrdens = ({ ordemInicial, onApontamentoCompleted }: Props): JSX.Element => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [peca, setPeca] = useState<Peca>(EMPTY_PECA);
    const [componentes, setComponentes] = useState<Componente[]>([]);
    const [loadingComp, setLoadingComp] = useState<boolean>(false);
    const [matricula, setMatricula] = useState<string>("");
    const [matriculaSAP, setMatriculaSAP] = useState<string>("");
    const [apontando, setApontando] = useState<boolean>(false);
    const [SelectMotivoRemocao, setSelectMotivoRemocao] = useState<string>("");

    const [modalLimparLote, setModalLimparLote] = useState<{ show: boolean; index: number | null }>({
        show: false,
        index: null
    });
    const [checkboxRemocao, setCheckboxRemocao] = useState(false);

    const [isMatriculaModalOpen, setIsMatriculaModalOpen] = useState(false);
    const [matriculaTemp, setMatriculaTemp] = useState<string>("");
    const [nomeUsuario, setNomeUsuario] = useState<string>("");
    const [validandoMatricula, setValidandoMatricula] = useState<boolean>(false);

    // Controle da busca automática (debounce) ao digitar a ordem
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const ordemBuscadaRef = useRef<string>(""); // última ordem já buscada (evita re-busca que apagaria os lotes)

    // Valida a matrícula SAP contra o backend. Retorna { nome, matriculaSAP } se existir, senão null.
    const validarMatriculaSAP = useCallback(async (matricula: string): Promise<{ nome: string; matriculaSAP: string } | null> => {
        const valor = matricula.trim();
        if (!/^\d{6}$/.test(valor)) return null;
        try {
            const { data } = await blogFetch.get(
                `IAcompanhamentoMontagem/validate-matricula/${encodeURIComponent(valor)}`
            );
            if (!data?.exists) return null;
            return {
                nome: data.nome ?? "",
                matriculaSAP: data.matricula_SAP ?? "",
            };
        } catch (err) {
            // 404 (exists:false) cai aqui via axios; tratamos como inválida
            return null;
        }
    }, []);

    useEffect(() => {
        const matriculaStorage = localStorage.getItem("listaMontagem_matriculas_SAP");
        if (!matriculaStorage) {
            console.warn("Matrícula não encontrada no localStorage");
            return;
        }
        let valor = "";
        let nomeSalvo = "";
        let matriculaSAPSalva = "";
        try {
            const obj = JSON.parse(matriculaStorage);
            valor = obj.principal || obj.secundaria || "";
            nomeSalvo = obj.nome || "";
            matriculaSAPSalva = obj.matriculaSAP || "";
        } catch {
            valor = matriculaStorage;
        }
        if (!valor) return;

        setMatricula(valor);
        setNomeUsuario(nomeSalvo); // mostra o nome salvo enquanto re-valida
        setMatriculaSAP(matriculaSAPSalva);

        // Re-valida contra o backend
        (async () => {
            const resultado = await validarMatriculaSAP(valor);
            if (resultado === null) {
                // matrícula deixou de ser válida → bloqueia
                localStorage.removeItem("listaMontagem_matriculas_SAP");
                setMatricula("");
                setNomeUsuario("");
                setMatriculaSAP("");
                toast.error("Matrícula não encontrada no SAP");
            } else {
                setNomeUsuario(resultado.nome);
                setMatriculaSAP(resultado.matriculaSAP);
                localStorage.setItem(
                    "listaMontagem_matriculas_SAP",
                    JSON.stringify({ principal: valor, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
                );
            }
        })();
    }, [validarMatriculaSAP]);

    const resetForm = useCallback(() => {
        setPeca(EMPTY_PECA);
        setComponentes([]);
        setLoadingComp(false);
        ordemBuscadaRef.current = "";
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }, []);

    // Aceita apenas números, máximo 6 dígitos
    const handleMatriculaChange = (valor: string) => {
        setMatriculaTemp(valor.replace(/\D/g, "").slice(0, 6));
    };

    const handleSalvarMatricula = async () => {
        const valor = matriculaTemp.trim();
        if (!/^\d{6}$/.test(valor)) {
            toast.error("A matrícula SAP deve ter 6 dígitos numéricos.");
            return;
        }
        setValidandoMatricula(true);
        const resultado = await validarMatriculaSAP(valor);
        setValidandoMatricula(false);

        if (resultado === null) {
            toast.error("Matrícula não encontrada no SAP");
            return; // não salva → Confirmar Apontamento permanece bloqueado
        }

        localStorage.setItem(
            "listaMontagem_matriculas_SAP",
            JSON.stringify({ principal: valor, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
        );
        setMatricula(valor);
        setNomeUsuario(resultado.nome);
        setMatriculaSAP(resultado.matriculaSAP);
        setMatriculaTemp("");
        setIsMatriculaModalOpen(false);
        toast.success(`Matrícula registrada: ${resultado.nome}`);
    };

    const handleLoteChange = async (idx: number, value: string) => {
        setComponentes((prev) =>
            prev.map((item, i) =>
                i === idx ? { ...item, lote: value } : item
            )
        );

        try {
            const comp = componentes[idx];
            if (!comp) return;

            if (value.trim().length >= 10) {
                const { data } = await blogFetch.get(`IAcompanhamentoMontagem/estoque/${encodeURIComponent(value)}`);

                setComponentes((prev) =>
                    prev.map((item, i) =>
                        i === idx ? { ...item, loteInvalido: true, veioDoBanco: data } : item
                    )
                );

                if (!data) toast.error(`Não existe estoque para o lote ${value}`);
            }
        } catch (err) {
            console.error('Erro ao verificar estoque do lote:', err);
        }
    };

    const abrirModalLimpar = (idx: number) => {
        setCheckboxRemocao(false);
        setModalLimparLote({ show: true, index: idx });
    };

    const confirmarLimpezaLote = async () => {
        if (modalLimparLote.index !== null) {
            const componenteAlvo = componentes[modalLimparLote.index];

            try {
                // Payload para enviar ao back-end
                const dadosRemocao = {
                    ordem: peca.ordem,
                    material: componenteAlvo.desenho_CodMat,
                    lote: componenteAlvo.lote,
                    CorteManual: checkboxRemocao,
                    matricula: matricula,
                    motivoRemocao: SelectMotivoRemocao || "NÃO INFORMADO",
                    pacote_key: componenteAlvo.pacitem_key || null
                };

                await blogFetch.post("IAcompanhamentoMontagem/Refugo", dadosRemocao);

                toast.success("Notificação de remoção enviada ao servidor.");
                setSelectMotivoRemocao("");
                setCheckboxRemocao(false);

            } catch (error) {
                console.error("Erro ao enviar remoção para o back-end:", error);
                toast.error("Erro ao comunicar remoção ao servidor, mas o lote será limpo localmente.");
                setSelectMotivoRemocao("");
                setCheckboxRemocao(false);
            }


            // LIMPEZA LOCAL (Acontece independente do checkbox, ou após o envio)
            setComponentes((prev) =>
                prev.map((item, i) =>
                    i === modalLimparLote.index
                        ? {
                            ...item,
                            lote: "",
                            loteRefugo: undefined,
                            loteInvalido: false,
                            veioDoBanco: false // Destrava o input
                        }
                        : item
                )
            );

            if (!checkboxRemocao) {
                toast.success("Lote removido da tela com sucesso!");
            }
        }

        // Fecha o modal e reseta estados
        setModalLimparLote({ show: false, index: null });
        setCheckboxRemocao(false);
    };

    const cancelarLimpezaLote = () => {
        setModalLimparLote({ show: false, index: null });
    };

    useEffect(() => {
        if (ordemInicial) {
            buscarDadosOrdem(ordemInicial);
        }
    }, [ordemInicial]);

    const buscarDadosOrdem = async (ordem: string) => {
        const ordemLimpa = ordem.trim();
        if (!ordemLimpa) return;

        // Cancela qualquer busca automática pendente e registra esta como a última buscada,
        // para o debounce não disparar de novo e apagar os lotes já preenchidos.
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        ordemBuscadaRef.current = ordemLimpa;

        try {
            const { data: pecaData } = await blogFetch.get(`IAcompanhamentoMontagem/peca/${ordemLimpa}`);

            if (!pecaData) {
                toast.error("Peça não encontrada.");
                return;
            }

            // Sincroniza a ordem efetivamente carregada (pode diferir do texto digitado,
            // ex.: zeros à esquerda) para o debounce não re-buscar e apagar os lotes.
            ordemBuscadaRef.current = String(pecaData.ordem_Serial ?? ordemLimpa).trim();

            setPeca({
                ordem: pecaData.ordem_Serial,
                chaveModApont: pecaData.ordem_ClaveAtual,
                proximoCenTrab: pecaData.ordem_SetAtual,
                tipo: `${pecaData.ordem_Part_Cod} - ${pecaData.ordem_Part_Short}`,
                lote: pecaData.pedido_Codigo,
                material: pecaData.ordem_CodMaterial,
                descricao: pecaData.ordem_VehDesc,
            });

            setIsModalOpen(false);
            setLoadingComp(true);

            const { data: compData } = await blogFetch.get(`IAcompanhamentoMontagem/componentes/${ordemLimpa}`);
            const lista = Array.isArray(compData) ? compData : compData?.rows || [];

            // AQUI ESTÁ O TRUQUE: Calculamos 'veioDoBanco' aqui
            setComponentes(lista.map((it: any) => ({
                ...it,
                loteInvalido: false,
                // Se tem lote preenchido na carga inicial, veio do banco e deve travar
                veioDoBanco: (it.lote && it.lote.trim().length > 0) ? true : false
            })));

        } catch (error: any) {
            toast.error(error.response?.data || "Erro na comunicação com o servidor.");
            resetForm();
        } finally {
            setLoadingComp(false);
        }
    };

    // Busca imediata acionada pelo botão "Buscar" ou pela tecla Enter no modal
    const handleBuscarOrdem = () => {
        const ordem = peca.ordem.trim();
        if (!ordem) {
            toast.error("Digite o número da ordem.");
            return;
        }
        buscarDadosOrdem(ordem);
    };

    // Leitura de QR Code no modal: preenche a ordem e busca imediatamente
    const handleLeituraQR = (codigo: string) => {
        const ordem = codigo.trim();
        if (!ordem) return;
        setPeca(prev => ({ ...prev, ordem }));
        buscarDadosOrdem(ordem);
    };

    const handleApontar = async () => {
        if (apontando) return; // guarda contra cliques múltiplos enquanto a requisição está em andamento

        if (!matricula) {
            toast.error("Registre uma matrícula SAP");
            return;
        }

        setApontando(true);
        try {
            const ordemAtual = peca.ordem.trim().replace(/^0+/, "");
            try {
                const { data: apontamentos } = await blogFetch.get(
                    "ISAGA_ApontamentoSAP/getApontamentoSAPByCentro/14MONTAG"
                );
                const lista: Array<{ ordem_Fabricacao?: string; _ts_Creat?: string }> =
                    Array.isArray(apontamentos) ? apontamentos : [];
                const agora = Date.now();
                const trintaMin = 30 * 60 * 1000;
                const duplicado = lista.some(ap => {
                    const apOrdem = (ap.ordem_Fabricacao || "").trim().replace(/^0+/, "");
                    if (apOrdem !== ordemAtual || !ap._ts_Creat) return false;
                    const diff = agora - new Date(ap._ts_Creat).getTime();
                    return diff >= 0 && diff < trintaMin;
                });
                if (duplicado) {
                    toast.error("Esta ordem já foi apontada há menos de 30 minutos");
                    return;
                }
            } catch (err) {
                console.error("Erro ao validar apontamentos recentes:", err);
            }

            try {
                const componentesComRefugo = componentes.map(c => ({
                    ...c,
                    loteRefugo: c.loteRefugo || null,
                }));

                const dadosApontamento = {
                    peca,
                    componentes: componentesComRefugo,
                    matricula: matricula,
                    matriculaSAP: matriculaSAP,
                };

                await blogFetch.post("IAcompanhamentoMontagem/apontar", dadosApontamento);
                toast.success("Apontamento realizado com sucesso!");

                await blogFetch.post("IAcompanhamentoMontagem/acompanhamento", {
                    Ordem: peca.ordem,
                    matricula: matricula,
                });

                resetForm();
                onApontamentoCompleted?.();
            } catch (err) {
                console.error("Erro ao realizar apontamento:", err);
                toast.error("Erro ao realizar apontamento.");
            }
        } finally {
            setApontando(false);
        }
    };

    const existeLoteInvalido = componentes.some(c => !c.lote || c.lote.trim().length < 9);

    // Destaque amarelo quando a Chave Modelo de Apontamento não for de montagem ("MONTAGE"/"MONTAGEM")
    const chvModApontDestaque = !!peca.chaveModApont && !peca.chaveModApont.trim().toUpperCase().startsWith("MONTAG");

    useEffect(() => {
        const ordem = peca.ordem.trim();
        if (!ordem) return;
        // Não re-busca uma ordem já carregada (evita apagar os lotes preenchidos)
        if (ordem === ordemBuscadaRef.current) return;

        debounceRef.current = setTimeout(() => buscarDadosOrdem(ordem), 3000);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [peca.ordem]);

    return (
        <div className="container-fluid p-0">
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-white py-3">
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        <div className="flex-grow-1 flex-shrink-1" style={{ flexBasis: 0, minWidth: "180px" }}>
                            <h5 className="mb-0 fw-bold text-secondary">Resumo de Ordens</h5>
                        </div>
                        <Table
                            bordered
                            size="sm"
                            className="mb-0 align-middle mx-auto"
                            style={{ width: "auto", minWidth: "320px", fontSize: "1.05rem" }}
                        >
                            <thead className="table-light text-center">
                                <tr>
                                    <th className="px-3 fw-bold">
                                        <i className="bi bi-person-badge me-1"></i> Operador
                                    </th>
                                    <th className="px-3 fw-bold">
                                        Matrícula SAP
                                    </th>
                                    <th className="px-2 fw-bold">
                                        Ação
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="px-3 text-center text-dark">
                                        {nomeUsuario || <span className="text-muted fst-italic">Não identificado</span>}
                                    </td>
                                    <td className="px-3 text-center text-dark">
                                        {matricula || <span className="text-muted">—</span>}
                                    </td>
                                    <td className="px-2 text-center">
                                        <Button
                                            variant={matricula ? "outline-secondary" : "warning"}
                                            size="sm"
                                            onClick={() => { setMatriculaTemp(matricula); setIsMatriculaModalOpen(true); }}
                                            title={matricula ? "Alterar matrícula" : "Registrar matrícula"}
                                            className="d-inline-flex align-items-center"
                                        >
                                            <i className={`bi ${matricula ? "bi-pencil-square" : "bi-person-plus-fill"} me-1`}></i>
                                            {matricula ? "Alterar" : "Registrar"}
                                        </Button>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                        <div className="flex-grow-1 flex-shrink-1 d-flex justify-content-end" style={{ flexBasis: 0, minWidth: "180px" }}>
                            <Button
                                variant="primary"
                                onClick={() => { resetForm(); setIsModalOpen(true); }}
                                className="d-inline-flex align-items-center rounded-pill px-4 shadow-sm"
                                style={{ fontSize: "0.95rem", fontWeight: 500 }}
                            >
                                <i className="bi bi-plus-circle-fill me-2" style={{ fontSize: "1.1rem" }}></i>
                                Adicionar Ordem
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive" style={{ maxHeight: "300px" }}>
                        <Table
                            hover
                            striped
                            bordered
                            size="sm"
                            className="mb-0 align-middle"
                            style={{ fontSize: "1.05rem" }}
                        >
                            <thead className="table-light text-center">
                                <tr>
                                    <th>Ordem</th>
                                    <th>Chv. Mod. Apont</th>
                                    <th>Próximo Cen. Trab</th>
                                    <th>Tipo Peça</th>
                                    <th>Lote</th>
                                    <th>Material</th>
                                    <th>Descrição</th>
                                </tr>
                            </thead>
                            <tbody>
                                {peca.ordem ? (
                                    <tr>
                                        <td className="text-center">{peca.ordem}</td>
                                        <td
                                            className={`text-center ${chvModApontDestaque ? "fw-bold" : ""}`}
                                            style={chvModApontDestaque ? { backgroundColor: "#ffe600", color: "#000" } : undefined}
                                        >
                                            {peca.chaveModApont}
                                        </td>
                                        <td className="text-center">{peca.proximoCenTrab}</td>
                                        <td className="text-center">{peca.tipo}</td>
                                        <td className="text-center">{peca.lote}</td>
                                        <td className="text-center">{peca.material}</td>
                                        <td className="text-center fw-bold">{peca.descricao}</td>
                                    </tr>
                                ) : (
                                    <tr><td colSpan={7} className="text-center text-muted py-4">Nenhuma ordem selecionada</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm">
                <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 fw-bold text-secondary">Consumo de Componentes</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table
                            hover
                            striped
                            bordered
                            size="sm"
                            className="mb-0 align-middle"
                            style={{ fontSize: "1.05rem" }}
                        >
                            <thead className="table-light text-center">
                                <tr>
                                    <th>#</th>
                                    <th>Ordem</th>
                                    {/* <th>Operação</th> */}
                                    {/* <th>Chv. Mod. Apont</th> */}
                                    <th>Posição</th>
                                    <th>Componente</th>
                                    <th>Descrição</th>
                                    <th>Qtd. UM</th>
                                    <th>UM</th>
                                    <th>Depósito</th>
                                    <th style={{ minWidth: "320px" }}>Lote</th>
                                    <th>Z_Tipo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingComp ? (
                                    <tr>
                                        <td colSpan={12} className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                        </td>
                                    </tr>
                                ) : componentes.length > 0 ? (
                                    componentes.map((c, index) => (
                                        <tr key={c.desenho_Key ?? index} className={c.loteInvalido ? 'table-danger' : ''}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{c.desenho_OrdemSerial}</td>
                                            {/* <td className="text-center">{c.desenho_Operation}</td> */}
                                            {/* <td>{c.desenho_ClaveModelo}</td> */}
                                            <td>{c.adicionaL_1}</td>
                                            <td className="text-center">{c.desenho_CodMat?.replace(/^0+/, '')}</td>
                                            <td className="text-center fw-bold">{c.desenho_Descricao}</td>
                                            <td className="text-center">{c.adicionaL_2?.trim()}</td>
                                            <td className="text-center">{c.adicionaL_3}</td>
                                            <td className="text-center">{c.adicionaL_5}</td>
                                            <td>
                                                <div className="d-flex gap-1 align-items-center">
                                                    <input
                                                        type="text"
                                                        className={`form-control form-control-sm ${c.loteInvalido === false ? '' : 'border-danger'}`}
                                                        value={c.lote ?? ""}
                                                        /* AQUI: Se veio do banco é TRUE, então disabled é TRUE */
                                                        disabled={c.veioDoBanco === true || !!c.loteRefugo}
                                                        onChange={(e) => handleLoteChange(index, e.target.value)}
                                                        placeholder="Insira o lote"
                                                    />
                                                    {/* Mostra o botão de lixeira se tiver lote (mesmo travado) para poder limpar */}
                                                    {c.lote && c.lote.trim().length > 0 && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            type="button"
                                                            onClick={() => abrirModalLimpar(index)}
                                                            title="Remover lote"
                                                        >
                                                            <DeleteForeverIcon fontSize="small" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-center">{c.desenho_ZTipo}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={12} className="text-center text-muted py-4">Aguardando dados...</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-end mt-4">
                <Button
                    variant="success"
                    className="px-5 fw-bold shadow-sm"
                    onClick={handleApontar}
                    disabled={existeLoteInvalido || componentes.length === 0 || apontando}
                >
                    {apontando ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Apontando...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-circle me-2"></i>
                            Confirmar Apontamento
                        </>
                    )}
                </Button>
            </div>

            <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h6 fw-bold">Adicionar Nova Ordem</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4">
                    <div className="form-group">
                        <label className="form-label fw-semibold text-muted small">NÚMERO DA ORDEM</label>
                        <input
                            type="text"
                            className="form-control form-control-lg text-primary fw-bold"
                            placeholder="Ex: 123456"
                            value={peca.ordem}
                            autoFocus
                            onChange={(e) => setPeca(prev => ({ ...prev, ordem: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") handleBuscarOrdem(); }}
                        />
                    </div>

                    <QRCodeReaderOrdem onLeitura={handleLeituraQR} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleBuscarOrdem}
                        disabled={!peca.ordem.trim() || loadingComp}
                        className="d-inline-flex align-items-center"
                    >
                        <i className="bi bi-search me-2"></i>
                        Buscar
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={isMatriculaModalOpen} onHide={() => setIsMatriculaModalOpen(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h6 fw-bold">Registrar Matrícula SAP</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4">
                    <div className="form-group">
                        <label className="form-label fw-semibold text-muted small">MATRÍCULA SAP (6 dígitos)</label>
                        <input
                            type="text"
                            className="form-control form-control-lg text-primary fw-bold text-center"
                            placeholder="Ex: 123456"
                            value={matriculaTemp}
                            autoFocus
                            maxLength={6}
                            onChange={(e) => handleMatriculaChange(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSalvarMatricula(); }}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setIsMatriculaModalOpen(false)}>Cancelar</Button>
                    <Button
                        variant="primary"
                        onClick={handleSalvarMatricula}
                        disabled={matriculaTemp.length !== 6 || validandoMatricula}
                    >
                        {validandoMatricula ? "Validando..." : "Salvar"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={modalLimparLote.show} onHide={cancelarLimpezaLote} centered backdrop="static">
                <Modal.Header closeButton className="bg-danger bg-opacity-10 border-danger">
                    <Modal.Title className="h6 fw-bold text-danger d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Confirmar Remoção
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4">
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="bi bi-trash3 text-danger" style={{ fontSize: "3rem" }}></i>
                        </div>
                        <h6 className="fw-bold mb-2">Tem certeza que deseja remover este lote?</h6>
                        <p className="text-muted mb-0">
                            {modalLimparLote.index !== null && componentes[modalLimparLote.index] && (
                                <>
                                    <strong>Componente:</strong> {componentes[modalLimparLote.index].desenho_CodMat}
                                    <br />
                                    <strong>Lote atual:</strong> {componentes[modalLimparLote.index].lote}
                                </>
                            )}
                        </p>

                        <div className="mt-4 text-start bg-light p-3 rounded">
                            <Form.Check
                                type="checkbox"
                                id="checkbox-remover-opcao"
                                label="Corte Manual - Remover Lote"
                                checked={checkboxRemocao}
                                onChange={(e) => setCheckboxRemocao(e.target.checked)}
                                className="fw-semibold text-secondary"
                            />
                        </div>

                        <div className="mt-4 text-start bg-light p-3 rounded">
                            <Form.Select
                                id="select-motivo-remocao"
                                value={SelectMotivoRemocao}
                                onChange={(e) => setSelectMotivoRemocao(e.target.value)}
                                className="fw-semibold text-secondary"
                            >
                                <option selected value="">Selecione o motivo</option>
                                <option value="Z27_DESCARTE">Z27 DESCARTE MATERIAL MODULADO</option>
                                <option value="Z27_CAMADA">Z27 CAMADA FALTANTE</option>
                                <option value="Z25_ERRO">Z25 ERRO DE ARQUIVO</option>
                                <option value="Z23_MENOR">Z23 MATERIAL MENOR</option>
                                <option value="Z21_ABRASAO">Z21 ABRASÃO</option>
                                <option value="Z21_NAO_ENCONTRADO">Z21 MATERIAL NÃO ENCONTRADO</option>
                                <option value="Z21_MANCHADO">Z21 MATERIAL MANCHADO</option>
                                <option value="Z21_MARCA_COLA">Z21 MARCA DE COLA</option>
                                <option value="Z19_CONTAMINADO">Z19 MATERIAL CONTAMINADO</option>
                                <option value="Z19_MARCA_LINER">Z19 MARCA DE LINER</option>
                            </Form.Select>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={cancelarLimpezaLote} className="px-4">
                        <i className="bi bi-x-lg me-2"></i>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={confirmarLimpezaLote} className="px-4 fw-bold">
                        <i className="bi bi-trash3-fill me-2"></i>
                        Sim, Remover
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};